import {
  DianaDataType,
  getAppliedFilters,
  getNextPageParam,
  getPeopleSidePanelKey,
  getThingsSidePanelKey,
  getWorksFromData as getThingsWorksFromData,
  IFlow,
  IProperty,
  IThingByTypeResponse,
  IWork,
  localForageUtil,
  makeDynamicEntitiesPageStore,
  PageViewModeEnum,
  performanceEntityQuery,
  SIDE_PEEK_ID_QUERY_PARAM,
  SortModel,
  useAbortRequestWhenFiltersValuesChanged,
  useAssetsPerformanceGraphQLParameters,
  useDisableLastVisitedWork,
  usePrevious,
  useServerAggregatedColumnOperation,
  useSidePeekDrawer,
} from '@diana/core';
import { GridSortModel } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { forwardRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Board } from '../../components/Board/Board';
import { getThingsGraphQLWorksQueryKey } from './Support';
import { ThingCard } from './ThingCard';
import { ThingsEmpty } from './ThingsEmpty';
import { ThingsTable } from './ThingsTable';

const CardHeight = 220;

export const Content = forwardRef<
  GridApiPremium,
  {
    flow: IFlow;
    supportedProperties: IProperty[];
    peoplePage: boolean;
    onSortModelChange: (model: GridSortModel) => void;
    sortModel: SortModel;
    dataGridStorageKey: string;
    showSidePeek: boolean;
  }
>((props, forwardedRef) => {
  const {
    flow,
    supportedProperties,
    peoplePage,
    onSortModelChange,
    sortModel,
    dataGridStorageKey,
    showSidePeek,
  } = props;
  const [searchParams] = useSearchParams();
  const useEntitiesPageStore = makeDynamicEntitiesPageStore(flow.flowOriginId);
  const flowId = flow.id;
  const availableFilters = useEntitiesPageStore((store) => store.availableFilters);
  const filtersValues = useEntitiesPageStore((store) => store.filtersValues);
  const appliedFilters = getAppliedFilters({ filtersValues });
  const viewMode = useEntitiesPageStore((store) => store.viewMode);
  const queryClient = useQueryClient();
  const isTableExpandedViewMode = viewMode === PageViewModeEnum.TableExpanded;
  const disableLastVisited = useDisableLastVisitedWork(flow);
  const isExpandedFeatureEnabled = isTableExpandedViewMode
    ? flow.featureFlags?.isExpandedFeatureEnabled
    : false;

  const { abort } = useAbortRequestWhenFiltersValuesChanged({
    filtersValues,
    sortModel,
    abortOnUnmount: false,
  });
  const { openThingDrawer } = useSidePeekDrawer();

  const { sortField, sortDirection, ...queryParameters } = useAssetsPerformanceGraphQLParameters({
    flow,
    filters: availableFilters,
    filtersValues,
    abort,
    isExpandedFeatureEnabled,
    sortModel,
  });

  const queryKeys = getThingsGraphQLWorksQueryKey(
    flowId,
    queryParameters.filterProperties,
    sortModel,
  );

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery<IWork[]>({
      queryKey: queryKeys,
      queryFn: async ({ pageParam }) => {
        return await performanceEntityQuery
          .get({ ...queryParameters, peoplePage })
          .fn({ skip: pageParam as number, sortField, sortDirection });
      },
      enabled: Boolean(flow),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 0,
      getNextPageParam: getNextPageParam,
    });

  const prevViewMode = usePrevious(viewMode);

  useEffect(() => {
    localForageUtil
      .getItem<string>({
        key: peoplePage
          ? getPeopleSidePanelKey(flow.flowOriginId)
          : getThingsSidePanelKey(flow.flowOriginId),
      })
      .then((openedSidePanelId) => {
        const id = (searchParams.get(SIDE_PEEK_ID_QUERY_PARAM) || openedSidePanelId) ?? null;

        if (id && showSidePeek) {
          openThingDrawer({ thingId: id, flowId, peoplePage, displayName: flow.displayName });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (viewMode === PageViewModeEnum.TableExpanded ||
        prevViewMode === PageViewModeEnum.TableExpanded) &&
      refetch
    ) {
      queryClient.removeQueries({ queryKey: queryKeys, exact: true });
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch, viewMode]);

  const aggregateColumnOperation = useServerAggregatedColumnOperation({
    filters: availableFilters,
    filtersValues,
    properties: supportedProperties,
    dianaDataType: DianaDataType.CompanyEntity,
    flows: [flow],
    showCompleted: true,
    showDeleted: false,
    showInProgress: true,
    isExpandedFeatureEnabled,
  });

  const entities = useMemo(() => getThingsWorksFromData({ data, flow }), [flow, data]);
  return (
    <>
      {viewMode === PageViewModeEnum.Board && (
        <Board<IWork>
          items={entities}
          itemHeight={CardHeight}
          isLoading={isLoading}
          isFetching={isFetching}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          EmptyComponent={<ThingsEmpty />}
          renderItem={({ item }) => (
            <ThingCard
              key={item.id}
              peoplePage={peoplePage}
              showSidePeek={showSidePeek}
              entity={item as unknown as IThingByTypeResponse}
              icon={flow.icon}
              imageUrl={flow.imageUrl}
              flowFeatures={flow.flowFeatures}
            />
          )}
        />
      )}
      {(viewMode === PageViewModeEnum.Table || viewMode === PageViewModeEnum.TableExpanded) && (
        <ThingsTable
          flow={flow}
          disableLastVisited={disableLastVisited}
          showSidePeek={showSidePeek}
          entities={entities}
          isLoading={isFetching}
          appliedFilters={appliedFilters}
          storageKey={dataGridStorageKey}
          ref={forwardedRef}
          isTableExpandedViewMode={isTableExpandedViewMode}
          supportedProperties={supportedProperties}
          peoplePage={peoplePage}
          fetchNextPage={fetchNextPage}
          onSortModelChange={onSortModelChange}
          aggregateColumnOperation={aggregateColumnOperation}
        />
      )}
    </>
  );
});
